import type { Action, PayloadAction } from '@reduxjs/toolkit';
import {
  CustomProperty,
  CustomPropertyEditFormValue,
  CustomPropertySearchFromData,
} from '@ducks/customProperty/types';

export type WorkState = {
  refresh: Record<string, never>;
  refresh2: Record<string, never>;
  refresh3: Record<string, never>;
  isLoading: boolean;
  isLoading2: boolean; // sub-loading
  isLoading3: boolean; // sub-loading(table内のLoading)
  isSubmitting: boolean;
  isNavigating: boolean;
  isNavigatingTo: number | null;
  works: WorksList[];
  worksAll: WorksList[];
  worksListInfo: WorksListInfo | null;
  worksItemData: WorksItemData | null;
  work: Work | null;
  workHistory: WorkHistory[];
  workHistoryDisplayParams: WorkHistoryDisplayParams;
  workInfo: WorkInfo | null;
  troubleReportPlaceholderRelation: TroubleReportPlaceholderRelation | null;
  selectWorkIdList: number[];
};

export enum WorkActionTypes {
  INIT = 'work/init',
  REFRESH = 'work/refresh',
  REFRESH2 = 'work/refresh2',
  REFRESH3 = 'work/refresh3',
  LOADING = 'work/set/isLoading',
  LOADING2 = 'work/set/isLoading2',
  LOADING3 = 'work/set/isLoading3',
  SUBMITTING = 'work/set/isSubmitting',
  NAVIGATING = 'work/set/isNavigating',
  NAVIGATING_TO = 'work/set/isNavigatingTo',
  SET_WORKS = 'work/set/works',
  SET_WORKS_ALL = 'work/set/works/all',
  SET_WORKS_LIST_INFO = 'work/set/worksListInfo',
  SET_WORKS_ITEM_DATA = 'work/set/worksItemData',
  SET_WORK = 'work/set/work',
  SET_WORK_HISTORY = 'work/set/workHistory',
  SET_WORK_HISTORY_DISPLAY_PARAMS = 'work/set/workHistoryDisplayParams',
  SET_WORK_INFO = 'work/set/workInfo',
  SET_TROUBLE_REPORT_PLACEHOLDERS = 'work/set/TroubleReportPlaceholders',
  SET_SELECT_WORK_ID_LIST = 'work/set/selectWorkIdList',
}

export type WorkAction =
  | Action<WorkActionTypes.INIT>
  | Action<WorkActionTypes.REFRESH>
  | Action<WorkActionTypes.REFRESH2>
  | Action<WorkActionTypes.REFRESH3>
  | PayloadAction<boolean, WorkActionTypes.LOADING>
  | PayloadAction<boolean, WorkActionTypes.LOADING2>
  | PayloadAction<boolean, WorkActionTypes.LOADING3>
  | PayloadAction<boolean, WorkActionTypes.SUBMITTING>
  | PayloadAction<boolean, WorkActionTypes.NAVIGATING>
  | PayloadAction<number | null, WorkActionTypes.NAVIGATING_TO>
  | PayloadAction<WorksList[], WorkActionTypes.SET_WORKS>
  | PayloadAction<WorksList[], WorkActionTypes.SET_WORKS_ALL>
  | PayloadAction<WorksListInfo | null, WorkActionTypes.SET_WORKS_LIST_INFO>
  | PayloadAction<WorksItemData | null, WorkActionTypes.SET_WORKS_ITEM_DATA>
  | PayloadAction<Work | null, WorkActionTypes.SET_WORK>
  | PayloadAction<WorkHistory[], WorkActionTypes.SET_WORK_HISTORY>
  | PayloadAction<WorkHistoryDisplayParams, WorkActionTypes.SET_WORK_HISTORY_DISPLAY_PARAMS>
  | PayloadAction<WorkInfo | null, WorkActionTypes.SET_WORK_INFO>
  | PayloadAction<TroubleReportPlaceholderRelation, WorkActionTypes.SET_TROUBLE_REPORT_PLACEHOLDERS>
  | PayloadAction<number[], WorkActionTypes.SET_SELECT_WORK_ID_LIST>;

// -- State Types ---------------------------------------------------------- //

export type WorksList = {
  /** 建物ID */
  propertyId: number;
  /** 建物名 */
  propertyName: string;
  /** 案件ID */
  troubleId: number;
  /** 計画名 */
  title: string;
  /** 優先度ID */
  priorityId: number;
  /** 優先度名 */
  priorityName: string;
  /** 作業分類ID */
  troubleType: number;
  /** 作業分類名 */
  troubleTypeName: string;
  /** 対応状況ID */
  statusId: number;
  /** 対応状況名 */
  statusName: string;
  /** 発生日 */
  occurrenceDate: string;
  /** 発生時間 */
  occurrenceTime: string;
  /** 承認日 */
  approvalDate: string;
  /** 説明 */
  description: string;
  /** 担当者ID */
  picUserId: number;
  /** 担当者名 */
  picUserName: string;
  /** 担当者ユーザの論理削除 */
  isDeletedUser: boolean;
  /** 最新の対応履歴 */
  latestComment: string;
  /** 最新更新者 */
  updateUserName: string;
  /** 最新更新日時 */
  updateDateTime: string;
  /** 期限 */
  dueDate: string;
  /** カスタムプロパティ */
  customPropertyList: CustomProperty[];
};

export type Work = {
  /** 案件ID */
  troubleId: number;
  /** 計画名 */
  title: string;
  /** 案件ID 不具合ID・作成日から生成されるID */
  matterId: string;
  /** 作業分類ID */
  troubleType: number;
  /** 作業分類名 */
  troubleTypeName: string;
  /** 期限 */
  dueDate: string;
  /** 優先度ID */
  priorityId: number;
  /** 優先度名 */
  priorityName: string;
  /** 対応状況ID */
  statusId: number;
  /** 対応状況名 */
  statusName: string;
  /** 建物ID */
  propertyId: number;
  /** 建物名 */
  propertyName: string;
  /** フロアID */
  floorId: number;
  /** フロア名 */
  floorName: string;
  /** 部屋ID */
  roomId: number;
  /** 部屋名 */
  roomName: string;
  /** 系統ID */
  diagramId: number;
  /** 系統名 */
  diagramName: string;
  /** 設備ID */
  equipmentId: number;
  /** 設備名 */
  equipmentName: string;
  /** 設備の論理削除 */
  isDeletedEquipment: false;
  /** 発生日 */
  occurrenceDate: string;
  /** 発生時間 */
  occurrenceTime: string | null;
  /** 承認日 */
  approvalDate: string;
  /** 担当者ID */
  picUserId: number;
  /** 担当者名 */
  picUserName: string;
  /** 担当者ユーザの論理削除 */
  isDeletedUser: boolean;
  /** 最新更新日時 */
  updateDateTime: string;
  /** 最新更新者 */
  updateUserName: string;
  /** 説明 */
  description: string;
  /** 添付画像 */
  imageList: workImageList[];
  /** 添付ファイル */
  fileList: FileList[];
  /** 点検表ID */
  inspectionChecklistId: number;
  /** 点検結果ID */
  inspectionResultId: number;
  /** コンポーネントUUID */
  componentId: string;
  /** コンポーネント名 */
  componentName: string;
  /** カスタムプロパティ */
  customPropertyList: CustomProperty[];
};

export type FileList = {
  fileName: string;
  fileUrl: string;
  updateDatetime: string;
  updateUserName: string;
  fileSize: number;
};

export type workImageList = {
  fileId: number;
  fileName: string;
  fileSize: number;
  fileUrl: string;
  isMovie: boolean;
  memo: string;
  thumbnailContent: string;
  thumnailUrl: string;
};

export type ImageList = {
  fileName: string;
  fileUrl: string;
  thumbnailUrl: string;
  memo: string;
  isMovie?: boolean;
  fileSize: number;
};

export type WorksListInfo = {
  totalHits: number;
  totalPages: number;
};

/**
 * 案件詳細検索に使用されるlocationのstateタイプ
 */
export interface WorkAdvancedQuery {
  page?: number;
  sort?: string;
  secondSort?: string;
  sizePerPage?: number;
  showCompleted: boolean;
  propertyId?: number;
  tag?: string | null;
  keyword?: string;
  type?: number;
  dueDateSince?: string;
  dueDateUntil?: string;
  priority?: number;
  status?: number;
  picUser?: string;
  approvalUser?: string;
  intervalSince?: string;
  intervalUntil?: string;
  startSince?: string;
  startUntil?: string;
  endSince?: string;
  endUntil?: string;
  updateSince?: string;
  updateUntil?: string;
  updateUser?: string;
  title?: string;
  customPropertySearchForm?: CustomPropertySearchFromData;
}

/** 案件検索時に使用されるlocationのstate */
export type WorksListQuery = WorkAdvancedQuery;

/**
 * 案件一覧画面のlocationのstate
 */

export type WorkListState = WorksListQuery &
  WorkAdvancedQuery & {
    order: boolean;
    pageH: number;
  };

export type WorksItemData = {
  troubleTypeList: {
    troubleTypeId: number;
    troubleTypeName: string;
  }[];
  troublePriorityList: {
    troublePriorityId: number;
    troublePriorityName: string;
  }[];
  troubleStatusList: {
    troubleStatusId: number;
    troubleStatusName: string;
  }[];
  diagramList: {
    diagramId: number;
    diagramName: string;
  }[];
  equipmentList: {
    equipmentId: number;
    equipmentName: string;
    diagramId: number | null;
    floorId: number | null;
    roomId: number | null;
  }[];
  floorList: {
    floorId: number;
    floorName: string;
    roomList: {
      roomId: number;
      roomName: string;
    }[];
  }[];
  userList: {
    userId: number;
    userName: string;
  }[];
};

// 案件登録
export type WorkCreateSubmitData = {
  title: string;
  troubleType: number;
  priorityId: number;
  statusId: number;
  dueDate: string | null;
  diagramId: number | null;
  equipmentId: number | null;
  floorId: number | null;
  roomId: number | null;
  occurrenceDate: string;
  occurrenceTime: string | null;
  picUserId: number;
  description: string;
  imageList: CreateImageList[];
  fileList: CreateFileList[];
  custom_property_list: CustomPropertyEditFormValue[];
};

export type CreateImageList = {
  fileName: string;
  fileContent: string;
  memo: string;
};

export type CreateFileList = {
  fileName: string;
  fileContent: string;
};

// 案件更新
export type WorkUpdateSubmitDate = {
  // trouble_id: number;
  status_id: number;
  title: string;
  trouble_type: number;
  priority_id: number;
  due_date: string | null;
  diagram_id: number | null;
  equipment_id: number | null;
  floor_id: number | null;
  room_id: number | null;
  occurrence_date: string;
  occurrence_time: string | null;
  pic_user_id: number;
  description: string;
  image_list: UpdateImageList[];
  file_list: UpdateFileList[];
  custom_property_list: CustomPropertyEditFormValue[];
};

export type UpdateImageList = {
  file_name: string;
  file_content: string;
  is_delete_file: boolean;
  memo: string;
};

export type UpdateFileList = {
  file_name: string;
  file_content: string;
  is_delete_file: boolean;
};

/** @summary 伝達事項編集type*/
export type WorkInfoMessage = {
  message: string;
};

export type WorkHistory = {
  troubleShootingId: number;
  troubleTypeId: number;
  troubleTypeName: string;
  iconId: number;
  iconName: string;
  responseUserId: number;
  responseUserName: string;
  isDeletedResponseUser: boolean;
  beforeStatus: number;
  beforeStatusName: string;
  afterStatus: number;
  afterStatusName: string;
  responseStartDate: string;
  responseStartTime: string;
  responseEndDate: string;
  responseEndTime: string;
  responseDetail: string;
  updateUserId: number;
  updateUserName: string;
  isDeletedUpdateUser: boolean;
  isOutputToReport: boolean;
  createDatetime: string;
  updateDatetime: string;
  fileList: FileList[];
  imageList: workImageList[];
};

export type WorkHistorySubmitData = {
  nextStatus: number;
  responseDetail: string;
  responseEndDate: string | null;
  responseEndTime: string | null;
  responseStartDate: string | null;
  responseStartTime: string | null;
  responseUserId: number | null;
  troubleShootingTypeId: number;
  isOutputToReport: boolean;
  fileList: {
    fileName: string;
    fileContent: string;
    deleteFlag: boolean;
  }[];
  imageList: {
    fileName: string;
    fileContent: string;
    memo: string;
    deleteFlag: boolean;
  }[];
};

// -- API Types ------------------------------------------------------------ //

export type APIDisplayParam = {
  readonly num_per_page: number;
  readonly current_page: number;
  readonly total_page: number;
  readonly total: number;
  readonly sort: number;
};

export type APIWorks = {
  /** 建物ID */
  readonly property_id: number;
  /** 建物名 */
  readonly property_name: string;
  /** 案件ID */
  readonly trouble_id: number;
  /** 計画名 */
  readonly title: string;
  /** 優先度ID */
  readonly priority_id: number;
  /** 優先度名 */
  readonly priority_name: string;
  /** 作業分類ID */
  readonly trouble_type: number;
  /** 作業分類名 */
  readonly trouble_type_name: string;
  /** 対応状況ID */
  readonly status_id: number;
  /** 対応状況名 */
  readonly status_name: string;
  /** 発生日 */
  readonly occurrence_date: string;
  /** 発生時間 */
  readonly occurrence_time: string;
  /** 承認日 */
  readonly approval_datetime: string;
  /** 説明 */
  readonly description: string;
  /** 担当者ID */
  readonly pic_user_id: number;
  /** 担当者名 */
  readonly pic_user_name: string;
  /** 担当者ユーザの論理削除 */
  readonly is_deleted_user: boolean;
  /** 最新の対応履歴 */
  readonly latest_comment: string;
  /** 最新更新者名 */
  readonly update_user_name: string;
  /** 最新更新日時 */
  readonly update_datetime: string;
  /** 期限 */
  readonly due_date: string;
  /** カスタムプロパティ */
  readonly custom_property_list: CustomProperty[];
};

export type APIWork = {
  /** 案件ID */
  readonly trouble_id: number;
  /** 計画名 */
  readonly title: string;
  /** 案件ID 不具合ID・作成日から生成されるID */
  readonly matter_id: string;
  /** 作業分類ID */
  readonly trouble_type: number;
  /** 作業分類名 */
  readonly trouble_type_name: string;
  /** 期限 */
  readonly due_date: string;
  /** 優先度ID */
  readonly priority_id: number;
  /** 優先度名 */
  readonly priority_name: string;
  /** 対応状況ID */
  readonly status_id: number;
  /** 対応状況名 */
  readonly status_name: string;
  /** 建物ID */
  readonly property_id: number;
  /** 建物名 */
  readonly property_name: string;
  /** フロアID */
  readonly floor_id: number;
  /** フロア名 */
  readonly floor_name: string;
  /** 部屋ID */
  readonly room_id: number;
  /** 部屋名 */
  readonly room_name: string;
  /** 系統ID */
  readonly diagram_id: number;
  /** 系統名 */
  readonly diagram_name: string;
  /** 設備ID */
  readonly equipment_id: number;
  /** 設備名 */
  readonly equipment_name: string;
  /** 設備の論理削除 */
  readonly is_deleted_equipment: false;
  /** 発生日 */
  readonly occurrence_date: string;
  /** 発生時間 */
  readonly occurrence_time: string;
  /** 承認日 */
  readonly approval_datetime: string;
  /** 担当者ID */
  readonly pic_user_id: number;
  /** 担当者名 */
  readonly pic_user_name: string;
  /** 担当者ユーザの論理削除 */
  readonly is_deleted_user: boolean;
  /** 最新更新日時 */
  readonly update_datetime: string;
  /** 最新更新者 */
  readonly update_user_name: string;
  /** 説明 */
  readonly description: string;
  /** 添付画像 */
  readonly image_list: WorkImageList[];
  /** 添付ファイル */
  readonly file_list: WorkFileList[];
  /** 点検表ID */
  readonly inspection_checklist_id: number;
  /** 点検結果ID */
  readonly inspection_result_id: number;
  /** コンポーネントID */
  readonly component_id: string;
  /** コンポーネント名 */
  readonly component_name: string;
  /** カスタムプロパティ */
  readonly custom_property_list: CustomProperty[];
};

export type WorkImageList = {
  file_id: number;
  file_name: string;
  file_size: number;
  file_url: string;
  is_movie: boolean;
  memo: string;
  thumbnail_content: string;
  thumnail_url: string;
};

export type WorkFileList = {
  file_name: string;
  file_url: string;
  update_datetime: string;
  update_user_name: string;
  file_size: number;
};

export type WorkInfo = {
  troubleId: number;
  title: string;
  propertyId: number;
  propertyName: string;
  message: string;
  files: WorkInfoFiles[];
};

export type WorkInfoFiles = {
  fileId: number;
  fileName: string;
  fileUrl: string;
  fileSize: number;
};

export class TroubleReportPlaceholderRelation {
  private placeHolders: TroubleReportPlaceholders[];
  private userFile: TroubleReportUserFile | null;

  constructor(apiResult: APIGetTroubleReportPlaceHoldersResponse | null) {
    if (apiResult == null || apiResult.result == null) {
      this.placeHolders = [];
      this.userFile = null;
      return;
    }

    this.placeHolders = apiResult.result.placeholder_list.map((p: TroubleReportPlaceholders) => ({
      name: p.name,
      value: p.value,
    }));
    this.userFile = apiResult.result.mapping;
  }

  get PlaceHolders(): TroubleReportPlaceholders[] {
    return this.placeHolders;
  }
  get HasUserFile(): boolean {
    return this.userFile != null;
  }
}

export type TroubleReportPlaceholders = {
  name: string;
  value: string;
};

export type TroubleReportUserFile = {
  file_id: number;
};

export type APIWorksItemData = {
  readonly trouble_type_list: {
    readonly trouble_type_id: number;
    readonly trouble_type_name: string;
  }[];
  readonly trouble_priority_list: {
    readonly trouble_priority_id: number;
    readonly trouble_priority_name: string;
  }[];
  readonly trouble_status_list: {
    readonly trouble_status_id: number;
    readonly trouble_status_name: string;
  }[];
  readonly diagram_list: {
    readonly diagram_id: number;
    readonly diagram_name: string;
  }[];
  readonly equipment_list: {
    readonly equipment_id: number;
    readonly equipment_name: string;
    readonly diagram_id: number | null;
    readonly floor_id: number | null;
    readonly room_id: number | null;
  }[];
  readonly floor_list: {
    readonly floor_id: number;
    readonly floor_name: string;
    readonly room_list: {
      readonly room_id: number;
      readonly room_name: string;
    }[];
  }[];
  readonly user_list: {
    readonly user_id: number;
    readonly last_name: string;
    readonly first_name: string;
  }[];
};

/** 不具合対応一覧取得（案件詳細：対応履歴一覧） */
export type APIWorkHistory = {
  readonly trouble_shooting_id: number;
  readonly trouble_type_id: number;
  readonly trouble_type_name: string;
  readonly icon_id: number;
  readonly icon_name: string;
  readonly response_user_id: number;
  readonly response_user_name: string;
  readonly is_deleted_response_user: boolean;
  readonly before_status: number;
  readonly before_status_name: string;
  readonly after_status: number;
  readonly after_status_name: string;
  readonly response_start_date: string;
  readonly response_start_time: string;
  readonly response_end_date: string;
  readonly response_end_time: string;
  readonly response_detail: string;
  readonly update_user_id: number;
  readonly update_user_name: string;
  readonly is_deleted_update_user: boolean;
  readonly is_output_to_report: boolean;
  readonly create_datetime: string;
  readonly update_datetime: string;
  readonly image_list: [
    {
      readonly file_id: number;
      readonly file_name: string;
      readonly file_size: number;
      readonly file_url: string;
      readonly is_movie: boolean;
      readonly memo: string;
      readonly thumbnail_content: string;
      readonly thumnail_url: string;
    },
  ];
  readonly file_list: [
    {
      readonly file_name: string;
      readonly file_url: string;
      readonly update_datetime: string;
      readonly update_user_name: string;
      readonly file_size: number;
    },
  ];
};

/**
 * @summary ページャ情報
 * @description 案件 - 対応履歴で使用する
 */
export type WorkHistoryDisplayParams = {
  /** 1ページあたりの件数 */
  num_per_page: number;
  /** 現在のページ */
  current_page: number;
  /** ページ数 */
  total_page: number;
  /** 全件数 */
  total: number;
  /** ソート番号 */
  sort: number;
} | null;

export type APICreateWork = {
  readonly title: string;
  readonly trouble_type: number;
  readonly priority_id: number;
  readonly status_id: number;
  readonly due_date: string | null;
  readonly diagram_id: number | null;
  readonly equipment_id: number | null;
  readonly floor_id: number | null;
  readonly room_id: number | null;
  readonly occurrence_date: string;
  readonly occurrence_time: string | null;
  readonly pic_user_id: number;
  readonly description: string;
  readonly image_list: APICreateWorkImageList[];
  readonly file_list: APICreateWorkFileList[];
  readonly custom_property_list: CustomPropertyEditFormValue[];
};

export type APICreateWorkImageList = {
  readonly file_name: string;
  readonly file_content: string;
  readonly memo: string;
};

export type APICreateWorkFileList = {
  readonly file_name: string;
  readonly file_content: string;
};

export type APIUpdateWork = {
  // readonly trouble_id: number;
  readonly status_id: number;
  readonly title: string;
  readonly trouble_type: number;
  readonly priority_id: number;
  readonly due_date: string | null;
  readonly diagram_id: number | null;
  readonly equipment_id: number | null;
  readonly floor_id: number | null;
  readonly room_id: number | null;
  readonly occurrence_date: string;
  readonly occurrence_time: string | null;
  readonly pic_user_id: number;
  readonly description: string;
  readonly image_list: APIUpdateWorkImageList[];
  readonly file_list: APIUpdateWorkFileList[];
  readonly custom_property_list: CustomPropertyEditFormValue[];
};

export type APIUpdateWorkImageList = {
  readonly file_name: string;
  readonly file_content: string;
  readonly is_delete_file: boolean;
  readonly memo: string;
};

export type APIUpdateWorkFileList = {
  readonly file_name: string;
  readonly file_content: string;
  readonly is_delete_file: boolean;
};

export type APICreateWorkHistory = {
  readonly after_status: number;
  readonly response_detail: string;
  readonly response_end_date: string | null;
  readonly response_end_time: string | null;
  readonly response_start_date: string | null;
  readonly response_start_time: string | null;
  readonly response_user_id: number | null;
  readonly trouble_type_id: number;
  readonly is_output_to_report: boolean;
  readonly file_list: {
    readonly file_name: string;
    readonly file_content: string;
  }[];
  readonly image_list: {
    readonly file_name: string;
    readonly file_content: string;
    readonly memo: string;
  }[];
};

export type APIUpdateWorkHistory = APICreateWorkHistory & {
  readonly file_list: {
    readonly is_delete_file: boolean;
  }[];
  readonly image_list: {
    readonly is_delete_file: boolean;
  }[];
};

// 不具合承認・差し戻し
export type APIApproveWorkHistory = {
  is_approved: boolean;
  comment: string;
  is_output_to_report?: boolean;
};

/** 不具合取り戻し */
export type APITakeBackWorkHistory = {
  comment: string;
  is_output_to_report?: boolean;
};

export type APIWorkInfo = {
  trouble_id: number;
  title: string;
  property_id: number;
  property_name: string;
  message: string;
  file_list: {
    file_id: number;
    file_name: string;
    file_url: string;
    file_size: number;
  }[];
};

export type APIGetWorksRequestParams = {
  /** ページ番号 */
  page: number;
  /** ソート番号 */
  sort: string;
  /** 1ページあたりの件数 */
  size_per_page: number;
  /** キーワード */
  keyword?: string;
  /** 完了の案件を表示するか */
  show_completed?: boolean;
  /** 建物ID */
  property_id?: number;
  /** タグIDの連結文字列 */
  tag?: string | null;
  /** 案件分類 */
  type?: number;
  /** 期限(から) */
  due_date_since?: string;
  /** 期限(まで) */
  due_date_until?: string;
  /** 優先度 */
  priority?: number;
  /** 対応状況 */
  status?: number;
  /** 担当者 */
  pic_user?: string;
  /** 承認者 */
  approval_user?: string;
  /** 作業期間(から) */
  interval_since?: string;
  /** 作業期間(まで) */
  interval_until?: string;
  /** 開始日(から) */
  start_since?: string;
  /** 開始日(まで) */
  start_until?: string;
  /** 終了日(から) */
  end_since?: string;
  /** 終了日(まで) */
  end_until?: string;
  /** 最新更新日時(から) */
  update_since?: string;
  /** 最新更新日時(まで) */
  update_until?: string;
  /** 最新更新者 */
  update_user?: string;
  /** 計画名 */
  title?: string;
};

export type APIGetWorksAllRequestParams = {
  /** ソート番号 */
  sort: string;
  /** キーワード */
  keyword?: string;
  /** 完了の案件を表示するか */
  show_completed?: boolean;
  /** 建物ID */
  property_id?: number;
  /** タグIDの連結文字列 */
  tag?: string | null;
  /** 案件分類 */
  type?: number;
  /** 期限(から) */
  due_date_since?: string;
  /** 期限(まで) */
  due_date_until?: string;
  /** 優先度 */
  priority?: number;
  /** 対応状況 */
  status?: number;
  /** 担当者 */
  pic_user?: string;
  /** 承認者 */
  approval_user?: string;
  /** 作業期間(から) */
  interval_since?: string;
  /** 作業期間(まで) */
  interval_until?: string;
  /** 開始日(から) */
  start_since?: string;
  /** 開始日(まで) */
  start_until?: string;
  /** 終了日(から) */
  end_since?: string;
  /** 終了日(まで) */
  end_until?: string;
  /** 最新更新日時(から) */
  update_since?: string;
  /** 最新更新日時(まで) */
  update_until?: string;
  /** 最新更新者 */
  update_user?: string;
  /** 計画名 */
  title?: string;
};

export type APIGetWorksResponse = {
  result: {
    trouble_list: APIWorks[];
    display_params: APIDisplayParam;
  };
};

export type APIGetWorksItemDataResponse = {
  result: APIWorksItemData;
};

export type APIGetWorkResponse = {
  result: APIWork;
};

/**
 * @summary 案件 - 対応履歴の取得APIで指定できるリクエストパラメータ
 */
export type APIGetWorkHistoryRequestParams = {
  /** ページ数 */
  page?: number;
  /** 1ページ当たりの件数 */
  size_per_page?: number;
  /** 全件取得フラグ */
  is_all?: boolean;
};

export type APIGetWorkHistoryResponse = {
  result: {
    trouble_shooting_list: APIWorkHistory[];
    display_params: WorkHistoryDisplayParams;
  };
};

export type APICreateWorkRequestParams = {
  property_id: number;
};
export type APICreateWorkRequest = APICreateWork;
export type APICreateWorkResponse = {
  result: { trouble_id: number };
};

export type APIUpdateWorkRequestParams = APICreateWorkRequestParams;
export type APIUpdateWorkRequest = APIUpdateWork;

export type APIDeleteWorksRequest = {
  trouble_list: { trouble_id: number }[];
};

export type APICreateWorkHistoryRequestParams = APICreateWorkRequestParams;
export type APICreateWorkHistoryRequest = APICreateWorkHistory;

export type APIUpdateWorkHistoryRequestParams = APICreateWorkHistoryRequestParams;
export type APIUpdateWorkHistoryRequest = APIUpdateWorkHistory;

export type APIExportWorkReportResponse = {
  message: string;
  result: {
    file_name: string;
    file_content: string;
  };
};

export type APIDownloadExcelFormatResponse = {
  result: {
    file_name: string;
    file_content: string;
  };
};

export type APIExportCsvFormatResponse = APIDownloadExcelFormatResponse;

export type APIGetWorkInformationResponse = {
  result: APIWorkInfo;
};

/** @summary  /api/constructions/{construction_id}/information/message(伝達事項編集)*/
export type APIPutWorkInfoMessageRequest = WorkInfoMessage;

export type APIApproveWorkHistoryRequest = APIApproveWorkHistory;

/** @summary  /api/troubles/{trouble_id}/uncompleted 不具合完了取り戻し */
export type APITakeBackWorkHistoryRequest = APITakeBackWorkHistory;

/** @summary  /api/troubles/format/placeholder 案件報告書フォーマットのプレースホルダー取得API*/
export type APIGetTroubleReportPlaceHolders = {
  readonly name: string;
  readonly value: string;
};

export type APIGetTroubleReportPlaceHoldersResponse = {
  result: {
    placeholder_list: APIGetTroubleReportPlaceHolders[];
    mapping: {
      file_id: number;
      property_id: number;
    };
  };
};

export type APIDownloadTroubleReportFormatResponse = {
  result: {
    file_name: string;
    file_content: string;
  };
};
